import {Box, Tab, Tabs} from '@mui/material';
import {DocumentMediaRedactions} from './media-redactions/DocumentMediaRedactions';
import {DocumentMediaRemarks} from './media-remarks/DocumentMediaRemarks';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {t} from 'i18next';
import {TabPanelProps} from '../../../../user-management/edit-user/EditUser';
import './DocumentMediaViewerItem.scss';
import {useMediaViewer} from './useMediaViewer';
import {DocumentMediaTranscripts} from './media-transcripts/DocumentMediaTranscripts';
import {
  IDocumentAudioFilePathItem,
  IDocumentMediaTranscriptRequestBody,
} from '../../../../../api/types/document.type';
import {isEmpty} from '../../../../../utils/formatters';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {useDocumentMediaTranscript} from '../../../../../hooks/document/useDocument';

export enum MediaTabType {
  Redactions = 'redactions',
  Transcripts = 'transcripts',
}

interface DocumentMediaViewerItemProps {
  audioFilePathDetails?: IDocumentAudioFilePathItem;
}

const MediaTabPanel = (props: Readonly<TabPanelProps>) => {
  const {children, value, index, ...other} = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`media-tabpanel-${index}`}
      aria-labelledby={`media-tab-${index}`}
      className={`media-tabpanel-active ${value === index ? '' : 'media-tabpanel-hidden'}`}
      {...other}
      data-testid={`document-media-viewer-item-div-tab-${index}`}
    >
      <div data-testid={`document-media-viewer-item-div-tab-child-${index}`}>
        {children}
      </div>
    </div>
  );
};

const DocumentMediaViewerItem: React.FC<DocumentMediaViewerItemProps> = ({
  audioFilePathDetails,
}) => {
  const activeDocument = useSelector(
    (state: RootState) => state.docView.activeDoc
  );

  const audioDocumentId = useMemo(
    () => parseInt(activeDocument?.documentId ?? ''),
    [activeDocument?.documentId]
  );

  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    setTabValue(0);
  }, [audioDocumentId]);

  const {
    loginUsername,
    annotationLayerId,
    showMediaRedaction,
    showMediaReviewerRemarks,
    showMediaReviewerRemarksPanel,
  } = useMediaViewer();

  const showMediaTranscription = useMemo(
    () => !isEmpty(audioFilePathDetails?.transcriptionFilePath),
    [audioFilePathDetails?.transcriptionFilePath]
  );

  const {
    mutate: getAudioTranscript,
    isPending: isTranscriptLoading,
    data: transcriptText = '',
  } = useDocumentMediaTranscript();

  const fetchMediaTranscript = useCallback(
    (
      mediaDocumentID: number,
      requestBody: IDocumentMediaTranscriptRequestBody
    ) => {
      if (
        !isEmpty(mediaDocumentID) &&
        !isEmpty(requestBody?.transcriptionFilePath)
      ) {
        getAudioTranscript({
          queryParams: {audioDocumentId: mediaDocumentID},
          requestBody: requestBody,
        });
      }
    },
    [getAudioTranscript]
  );

  const tabs = useMemo(() => {
    const transcriptTab = showMediaTranscription
      ? {
          id: MediaTabType.Transcripts,
          label: t('docTranscripts.transcripts'),
        }
      : undefined;

    const redactionsTab = showMediaRedaction
      ? {id: MediaTabType.Redactions, label: t('docRedactions.redactions')}
      : undefined;

    return [transcriptTab, redactionsTab].filter(Boolean);
  }, [showMediaRedaction, showMediaTranscription]);

  const handleChangeTab = useCallback(
    (_: React.SyntheticEvent, newTabValue: number) => {
      setTabValue(newTabValue);
    },
    []
  );

  const getLeftPanelWidth = useMemo(
    () =>
      showMediaReviewerRemarksPanel ? '61.7% !important' : '100% !important',
    [showMediaReviewerRemarksPanel]
  );

  const getRightPanelWidth = useMemo(
    () =>
      showMediaReviewerRemarksPanel ? '38.3% !important' : '0% !important',
    [showMediaReviewerRemarksPanel]
  );

  useEffect(() => {
    fetchMediaTranscript(audioDocumentId, {...(audioFilePathDetails! ?? {})});
  }, [audioFilePathDetails?.transcriptionFilePath, fetchMediaTranscript]);

  return (
    <Box
      data-testid="media-viewer-item"
      className={'doc-media-children-container'}
    >
      {tabs.length > 0 && (
        <Box
          className={'doc-media-left-container'}
          component={'div'}
          sx={{width: getLeftPanelWidth}}
        >
          <Box>
            <Tabs
              value={tabValue}
              onChange={handleChangeTab}
              aria-label="media-tabs"
              className="media-tabs"
            >
              {tabs.map((tab) => (
                <Tab
                  id={tab?.id}
                  aria-controls={`media-tabpanel-${tab?.id}`}
                  key={tab?.id}
                  label={tab?.label}
                  className="media-tab"
                  data-testid={tab?.id}
                />
              ))}
            </Tabs>
          </Box>
          <Box className={'divider'} />
          {tabs.map((tab, index) => (
            <MediaTabPanel key={tab?.id} value={tabValue} index={index}>
              {tab?.id === MediaTabType.Transcripts && (
                <DocumentMediaTranscripts
                  isTranscriptLoading={isTranscriptLoading}
                  transcriptText={transcriptText}
                  dataTestId={`${index}`}
                />
              )}
              {tab?.id === MediaTabType.Redactions && (
                <DocumentMediaRedactions />
              )}
            </MediaTabPanel>
          ))}
        </Box>
      )}

      {showMediaReviewerRemarks && (
        <Box
          className={'doc-media-right-container'}
          component={'div'}
          sx={{width: getRightPanelWidth}}
        >
          <DocumentMediaRemarks
            showRemarks={showMediaReviewerRemarksPanel}
            loginUsername={loginUsername}
            annotationLayerId={annotationLayerId}
          />
        </Box>
      )}
    </Box>
  );
};
export {DocumentMediaViewerItem};
